import axios from "axios";
import { API_BASE_URL, APIs, AuthRoutes, SHARED_SERVICEURL } from "../constants/Constants";
import axiosInterceptor from "../helpers/Interceptor";
import { getEnv } from "../helpers/EnvHelper";

/**
 * Hanging Protocol V2 API client.
 *
 * Groups and protocols travel as MedDream-shaped JSON — the server stores them
 * verbatim, so what goes out here is what ends up in the exported
 * hangingProtocolsV2 document.
 *
 * Scope is a body field rather than a separate endpoint pair (as v1 uses), so
 * every call takes it. The shared scopes are admin-only; USER is the caller's
 * own, and the server derives ownership from the token rather than the body.
 */

export const HP_SCOPE = {
  USER: "USER",
  /**
   * The shared set, admin-only. Saved like anything else and used by our own viewer at once;
   * the Advance Viewer is given it only when an admin publishes — see publishSystem.
   */
  SYSTEM: "SYSTEM",
};

/** Matches HpGroupKind on the server; maps to hp / hpDrag / hpComparison. */
export const HP_KIND = {
  STANDARD: "STANDARD",
  DRAG: "DRAG",
  COMPARISON: "COMPARISON",
};

/**
 * The share link this page was opened with, or null when a signed-in user is behind it.
 *
 * A shared study has no session — no `token` cookie, and nothing for refreshToken to refresh —
 * so every /jwtServices read answers 403. The share token in the URL is the credential instead,
 * and the endpoints that take it live under /sharedServices.
 *
 * Read from the URL rather than passed in: both callers of loadUserDocument sit in viewer
 * providers that never receive the token, and threading it through two context trees to reach a
 * mount effect would be a lot of plumbing for one string the address bar already holds.
 */
const sharedViewerLink = () => {
  if (!window.location.pathname.includes(AuthRoutes.SHAREDVIEWER)) return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get("id");
  return token ? { token, isActiveData: params.get("isActiveData") ?? "true" } : null;
};

const post = async (url, data) => {
  try {
    return await axiosInterceptor.post(API_BASE_URL + url, { data });
  } catch (error) {
    // Never on a share link: a visitor with no session cannot be logged out, and sending them
    // to the login page loses the study they were handed. They get the failed response instead,
    // which every caller here already reads as "no protocols".
    if (error.response?.status === 403 && !sharedViewerLink()) {
      window.location.assign(APIs.REDIRECTLOGOUT);
      return undefined;
    }
    return error.response;
  }
};

const succeeded = (response) => response?.status === 200 && response?.data?.result === "SUCCESS";

/**
 * Any write invalidates the cached document — see loadUserDocument — and a write to the
 * caller's own protocols then carries the change through to the Advance Viewer, see
 * syncAdvanceViewer.
 *
 * Only their own. A write to the shared set is saved and live in our own viewer either way,
 * but the Advance Viewer is not told: it holds a file we overwrite under whoever is reading
 * from it, and publishing is the deliberate act that does that. Otherwise a stray drag on the
 * shared list would rewrite every radiologist's file and end their viewer sessions.
 *
 * The sync is deliberately not awaited. The protocols are saved by the time this returns, and
 * a viewer that is slow or unreachable must not hold a radiologist's save open.
 */
const write = async (url, data) => {
  invalidateUserDocument();
  const response = await post(url, data);
  if (succeeded(response) && data?.scope === HP_SCOPE.USER) {
    if (deferredSyncDepth > 0) deferredSyncWanted = true;
    else syncAdvanceViewer();
  }
  return response;
};

/*
 * One publish for one Save.
 *
 * The group list commits a whole draft at once -- activations, deletions and both reorders --
 * which is several writes in a row. Each would otherwise publish the radiologist's document
 * again and end their viewer session again with it, and awaiting them in sequence defeats the
 * coalescing in syncAdvanceViewer: that merges the syncs which overlap, and these do not.
 */
let deferredSyncDepth = 0;
let deferredSyncWanted = false;

/**
 * Run `commit` as one unit of work: the writes inside it publish once, when the last of them
 * has landed, and only if one of them touched the caller's own protocols.
 *
 * A batch that failed part way through still publishes what did land -- a partly applied save
 * is the state the viewer should be holding, not the one before it.
 */
export const withDeferredSync = async (commit) => {
  deferredSyncDepth += 1;
  try {
    return await commit();
  } finally {
    deferredSyncDepth -= 1;
    if (deferredSyncDepth === 0 && deferredSyncWanted) {
      deferredSyncWanted = false;
      syncAdvanceViewer();
    }
  }
};

/* ----------------------------------------------------------------- reads */

/** Group summaries with their protocol summaries, in matching order. */
export const fetchGroups = (scope = HP_SCOPE.USER) => post(APIs.HPV2_GROUPS, { scope });

/** The group envelope: everything but its categories. */
export const fetchGroup = (id, scope = HP_SCOPE.USER) => post(APIs.HPV2_GROUP_GET, { id, scope });

/** One protocol (a MedDream "category"), complete. */
export const fetchProtocol = (id, scope = HP_SCOPE.USER) => post(APIs.HPV2_PROTOCOL_GET, { id, scope });

/* ---------------------------------------------------------------- writes */

export const saveGroup = (group, { scope = HP_SCOPE.USER, kind = HP_KIND.STANDARD } = {}) =>
  write(APIs.HPV2_GROUP_SAVE, { group, scope, kind });

export const saveProtocol = (groupId, protocol, scope = HP_SCOPE.USER) =>
  write(APIs.HPV2_PROTOCOL_SAVE, { groupId, protocol, scope });

export const deleteGroup = (id, scope = HP_SCOPE.USER) => write(APIs.HPV2_GROUP_DELETE, { id, scope });

export const deleteProtocol = (id, scope = HP_SCOPE.USER) => write(APIs.HPV2_PROTOCOL_DELETE, { id, scope });

export const setGroupActive = (id, active, scope = HP_SCOPE.USER) =>
  write(APIs.HPV2_GROUP_STATUS, { id, active, scope });

/* --------------------------------------------------------------- reorder */

/**
 * Order decides which protocol hangs — first applicable wins — so these change
 * behaviour, not just presentation. The server requires a full permutation:
 * a partial list would leave the rest at stale positions.
 */
export const reorderGroups = (orderedIds, scope = HP_SCOPE.USER) =>
  write(APIs.HPV2_GROUP_REORDER, { orderedIds, scope });

export const reorderProtocols = (groupId, orderedIds, scope = HP_SCOPE.USER) =>
  write(APIs.HPV2_PROTOCOL_REORDER, { groupId, orderedIds, scope });

export const moveProtocol = (id, targetGroupId, scope = HP_SCOPE.USER) =>
  write(APIs.HPV2_PROTOCOL_MOVE, { id, targetGroupId, scope });

/* -------------------------------------------------------------- document */

/** The assembled hangingProtocolsV2 document for one scope — an authoring read. */
export const exportDocument = (scope = HP_SCOPE.USER) => post(APIs.HPV2_DOCUMENT_EXPORT, { scope });

/**
 * What would hang for the caller: their own protocols with the system ones behind
 * them. This is the viewer's read — see loadUserDocument.
 */
export const fetchEffectiveDocument = () => post(APIs.HPV2_DOCUMENT_EFFECTIVE, {});

/**
 * The same read for a share-link visitor, with the share token standing in for the JWT cookie.
 *
 * What comes back is the shared (SYSTEM) protocols alone. A share link carries no identity to
 * layer a user's own protocols over, and the sharer's personal ones are not part of what sharing
 * a study hands over — so the recipient hangs on the tenant's shared set.
 *
 * Plain axios on purpose. axiosInterceptor answers a 403 by calling refreshToken and retrying,
 * which for a visitor with no session is a guaranteed second 403 and a wasted round trip; this
 * endpoint is permit-all and wants no cookie at all.
 */
export const fetchSharedEffectiveDocument = ({ token, isActiveData }) =>
  axios
    .post(
      API_BASE_URL + SHARED_SERVICEURL + APIs.GETSHAREDHPV2DOCUMENT,
      { data: { token, isActiveData } },
      { headers: { "content-type": "application/json" } }
    )
    .catch((error) => error.response);

/**
 * Replaces everything in the scope — the shared set for an admin, the caller's own protocols
 * for a radiologist. The counterpart to exportDocument.
 */
export const importDocument = (document, scope = HP_SCOPE.SYSTEM) =>
  write(APIs.HPV2_DOCUMENT_IMPORT, { document, scope });

/* -------------------------------------------------------------- delivery */

/**
 * Push the caller's protocols to the Advance Viewer.
 *
 * Always the caller's own — the viewer keys protocols per user, and what it receives is
 * their groups plus the system ones, since a per-user file replaces the shared set there
 * rather than adding to it.
 *
 * Nothing calls this directly: every edit goes through syncAdvanceViewer, which needs the
 * publish and the session reset to happen in that order.
 */
const publishDocument = () => post(APIs.HPV2_DOCUMENT_PUBLISH, {});

/* --------------------------------------------------- shared set: publish */

/**
 * Whether the saved shared protocols differ from the ones the Advance Viewer is being given,
 * and when they were last published.
 */
export const fetchSystemPublishStatus = () => post(APIs.HPV2_SYSTEM_STATUS, {});

/**
 * Give the Advance Viewer the shared protocols as they now stand.
 *
 * Not a save — those already happened, and our own viewer has been using them all along. This
 * moves the document the server delivers and writes it, in one place: the shared protocols are
 * a single file, read by every user who has authored nothing of their own, so there is no
 * fanning out to do and nothing per-radiologist to publish from here.
 *
 * The session reset is still the browser's, and only for the admin sitting here — their own
 * viewer session is holding the document from before. Everyone else reads the new file when
 * their next session starts; nothing we can call from this page would reach their session.
 */
export const publishSystem = async () => {
  const response = await post(APIs.HPV2_SYSTEM_PUBLISH, {});
  if (response?.status !== 200) return response;
  // The shared set has moved even where the viewer could not be written to -- our own viewer
  // reads it from the database, so the cached document is stale either way.
  invalidateUserDocument();
  resetAdvanceViewerSession(response.data?.data?.sessionResetUrl);
  return response;
};

/**
 * End this user's Advance Viewer session so a publish takes effect there.
 *
 * A publish writes MedDream's settings file directly, and MedDream never re-reads it: it
 * caches the protocol document in the user's session with no expiry, and evicts it only on a
 * save through its own API. So the viewer keeps hanging the protocols it loaded first, for
 * every study opened afterwards, until that session ends. Ending it is the whole fix — the
 * next study open authenticates again and reads the published file.
 *
 * The URL comes from the delivery status rather than from configuration here, so it always
 * names the same viewer the study links point at.
 *
 * Fire-and-forget by design. The response is opaque (cross-origin, and that host serves no
 * CORS headers), so there is nothing to read, and a viewer that cannot be reached must not
 * turn a successful publish into a visible failure. `credentials` is the point of the call:
 * MedDream's session cookie is SameSite=None, so it rides along on a cross-site request.
 */
export const resetAdvanceViewerSession = (sessionResetUrl) => {
  if (!sessionResetUrl) return;
  viewerSessionOrigins(sessionResetUrl).forEach((url) => {
    fetch(url, {
      method: "GET",
      credentials: "include",
      mode: "no-cors",
      cache: "no-store",
      keepalive: true,
    }).catch(() => {});
  });
};

/*
 * One sync at a time, with a trailing re-run.
 *
 * A single change is often several writes — a group saved and then its protocols, a drag that
 * reorders a list — and each would otherwise publish the whole document again and end the
 * viewer session again with it. Coalescing keeps a burst down to one publish; the trailing
 * re-run is what stops the last write in that burst from being the one that gets dropped.
 */
let syncInFlight = null;
let syncPending = false;

/**
 * Carry a saved change through to the Advance Viewer.
 *
 * Two steps, in this order and for a reason. The publish replaces the user's protocol file,
 * which MedDream does not notice — it caches the document in the session and re-reads nothing
 * — so that session has to end before the file is read. Ending it first would only cache the
 * old protocols again.
 *
 * Failures are silent. The save this follows has already succeeded, so there is nothing to
 * take back, and an unreachable viewer costs nothing lasting: the server rewrites this user's
 * file as their next study opens, which is the moment before the viewer reads it.
 */
const syncAdvanceViewer = () => {
  if (syncInFlight) {
    syncPending = true;
    return syncInFlight;
  }
  // Nothing awaits this, so a rejection here would surface as an unhandled one.
  syncInFlight = publishThenEndViewerSession().catch(() => null).finally(() => {
    syncInFlight = null;
    if (syncPending) {
      syncPending = false;
      syncAdvanceViewer();
    }
  });
  return syncInFlight;
};

const publishThenEndViewerSession = async () => {
  const response = await publishDocument();
  const result = succeeded(response) ? response.data?.data : null;
  // Delivery switched off for the environment, or a viewer that could not be written to:
  // either way there is no new file out there, so the session holds nothing stale.
  if (result?.delivered) resetAdvanceViewerSession(result.sessionResetUrl);
  return result;
};

/** Matches the slot count StudyViewerState hands out through advViewerSlots. */
const ADVANCE_VIEWER_SLOTS = 10;

/**
 * Every viewer origin this user could hold a session on.
 *
 * A study is not opened against the URL the server reports: StudyViewerState swaps
 * REACT_APP_ADVANCE_VIEWER_SUB_DOMAIN for `<subdomain><slot>` so ten viewers can be open at
 * once, and each numbered subdomain is a separate origin with its own session cookie. One
 * URL would end at most one of them.
 *
 * Every slot, not just the ones in use: a cookie outlives the slot being released, so a slot
 * handed out again later would go on serving the protocols it cached. Slots the user has
 * never opened have no cookie to send, which makes those calls harmless no-ops.
 *
 * Where the reported URL carries no such subdomain -- the test stacks point straight at a
 * MedDream host -- there is one origin and this is it.
 */
const viewerSessionOrigins = (sessionResetUrl) => {
  const subdomain = getEnv("REACT_APP_ADVANCE_VIEWER_SUB_DOMAIN");
  if (!subdomain || !sessionResetUrl.includes(subdomain)) return [sessionResetUrl];
  return Array.from({ length: ADVANCE_VIEWER_SLOTS }, (_unused, slot) =>
    sessionResetUrl.replace(subdomain, `${subdomain}${slot + 1}`)
  );
};

/**
 * The document the viewer matches against, fetched once and shared.
 *
 * The viewer needs it twice per study — to select priors (StudyViewerState,
 * before the stacks exist) and to hang (ViewportState) — and those live in
 * different providers, so neither can pass it to the other. Caching the promise
 * keeps that to one request; the writes above drop the cache, so an edit made
 * in the settings screen is picked up without a reload.
 *
 * The *effective* document, not the USER scope: system protocols hang studies
 * too, and reading only the caller's own left the basic viewer blind to every
 * shared protocol the Advance Viewer was hanging.
 *
 * On a share link there is no caller for it to be effective for, so this reads the
 * shared set over /sharedServices instead — see sharedViewerLink.
 */
let userDocument = null;

export const loadUserDocument = () => {
  if (!userDocument) {
    const shared = sharedViewerLink();
    userDocument = (shared ? fetchSharedEffectiveDocument(shared) : fetchEffectiveDocument())
      .then((response) =>
        response?.status === 200 && response.data?.result === "SUCCESS" ? response.data.data : null
      )
      .catch(() => null);
  }
  return userDocument;
};

export const invalidateUserDocument = () => {
  userDocument = null;
};
